import React, { Component } from 'react'
import { Dot, Text, Rectangle } from 'recharts'
import moment from 'moment-timezone'

import Loader from '../components/Loader'
import ActivePower from '../components/charts/ActivePower'
import AvailableDischargeEnergy from '../components/charts/AvailableDischargeEnergy'
import FrequencyResponse from '../components/charts/FrequencyResponse'
import GridFrequency from '../components/charts/GridFrequency'
// import FrequencyResponseAndGridFrequency from '../components/charts/FrequencyResponseAndGridFrequency'
import BaselinePowerSOC from '../components/charts/BaselinePowerSOC'
import Power from '../components/charts/Power'
import ImbalancePriceForecast from '../components/charts/ImbalancePriceForecast'
import StateOfCharge from '../components/charts/StateOfCharge'
import BalancingPrice from '../components/charts/BalancingPrice'
import Frequency from '../components/charts/Frequency'
import Imbalance from '../components/charts/Imbalance'
import Energy from '../components/charts/Energy'
import ImbalanceRevenueForecast from '../components/charts/ImbalanceRevenueForecast'
// import PowerFlows from '../components/charts/PowerFlows'
import ImbalancePrices from '../components/charts/ImbalancePrices'
import Revenue from '../components/charts/Revenue'
import ImbalanceRevenue from '../components/charts/ImbalanceRevenue'
import Status from '../components/Status'

import { getBatteryData, getFrequencyData, getEnergyData, getImbalance, getImbalancePrices, getFCR, getStatus, getSmappee, getRevenue, getDexterData } from '../api/battery'
import { stompSubscribe, stompUnsubscribe } from '../services/stomp/battery'

moment.tz.setDefault('Europe/Brussels')

const timeframes = [
    { frame: '5m', battery_refresh: 0, imbalance_refresh: 60, power_flows_refresh: 300, seconds: true },
    { frame: '30m', battery_refresh: 5, imbalance_refresh: 300, power_flows_refresh: 600, seconds: false },
    { frame: '1h', battery_refresh: 10, imbalance_refresh: 600, power_flows_refresh: 1200, seconds: false },
    { frame: '4h', battery_refresh: 30, imbalance_refresh: 1200, power_flows_refresh: 1800, seconds: false },
    { frame: '12h', battery_refresh: 60, imbalance_refresh: 1800, power_flows_refresh: 3600, seconds: false },
    { frame: '1d', battery_refresh: 120, imbalance_refresh: 3600, power_flows_refresh: 7200, seconds: false }
]

class Monitoring extends Component {
    constructor(props) {
        super(props)

        let timeframe = timeframes[0]

        try {
            timeframe = JSON.parse(window.localStorage.timeframe)
        }
        catch (err) { }

        this.state = {
            timeframe,
            loadingBatteryData: true,
            loadingFrequencyData: true,
            loadingEnergyData: true,
            loadingImbalanceData: true,
            loadingImbalancePricesData: true,
            loadingSmappeeData: true,
            loadingRevenueData: true,
            // loadingImbalancePriceForecastData: true,
            loadingBalancingPriceData: true,
            loadingImbalanceRevenueForecastData: true,
            stompSubscription: undefined,
            latestData: undefined,
            hidden: {},
            dynamicYAxis: {}
        }

        this.updateBatteryData = this.updateBatteryData.bind(this)
        this.updateFrequencyData = this.updateFrequencyData.bind(this)
        this.updateEnergyData = this.updateEnergyData.bind(this)
        this.updateImbalanceData = this.updateImbalanceData.bind(this)
        this.updateImbalancePricesData = this.updateImbalancePricesData.bind(this)
        this.updateSmappeeData = this.updateSmappeeData.bind(this)
        this.updateRevenueData = this.updateRevenueData.bind(this)
        this.updateStatus = this.updateStatus.bind(this)
        this.updateFCR = this.updateFCR.bind(this)
        // this.updateImbalancePriceForecastData = this.updateImbalancePriceForecastData.bind(this)
        this.updateBalancingPriceData = this.updateBalancingPriceData.bind(this)
        this.updateImbalanceRevenueForecastData = this.updateImbalanceRevenueForecastData.bind(this)
        this.setUpdates = this.setUpdates.bind(this)
        this.unsetUpdates = this.unsetUpdates.bind(this)
        this.tickFormatterX = this.tickFormatterX.bind(this)
        this.tickFormatterY = this.tickFormatterY.bind(this)
        this.onTimeframeChange = this.onTimeframeChange.bind(this)
        this.toggleChart = this.toggleChart.bind(this)
        this.toggleDynamicYAxis = this.toggleDynamicYAxis.bind(this)
        this.getLastUpdate = this.getLastUpdate.bind(this)
        this.setDot = this.setDot.bind(this)
        this.printValue = this.printValue.bind(this)
        this.getTooltip = this.getTooltip.bind(this)

        // create web worker for correct countdown (executes in a separate thread)
        const blob = new window.Blob([`setInterval(() => postMessage(''), 1000)`])
        this.objectURL = window.URL.createObjectURL(blob)
        this.worker = new window.Worker(this.objectURL)
    }

    componentDidMount() {
        window.$('[data-toggle="tooltip"]').tooltip()

        if (!this.props.site || (this.props.site && this.props.site.siteId !== this.props.siteId))
            this.props.selectSite(this.props.siteId)
        else {
            this.updateBatteryData()
            this.updateFrequencyData()
            this.updateEnergyData()
            this.updateImbalanceData()
            this.updateImbalancePricesData()
            this.updateSmappeeData()
            this.updateRevenueData()
            this.updateStatus()
            this.updateFCR()
            this.updateImbalancePriceForecastData()
            this.updateBalancingPriceData()
            this.updateImbalanceRevenueForecastData()
            this.setUpdates()
        }
    }

    componentWillUnmount() {
        this.unsetUpdates()
        this.worker.terminate()
        window.URL.revokeObjectURL(this.objectURL)
    }

    async updateBatteryData(cb) {

        try {
            let response = await getBatteryData(this.props.site.siteId, this.state.timeframe.frame)

            let batteryData = response.data
            let batteryStates = response.states ? response.states : {}

            let last = batteryData[batteryData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                batteryData,
                batteryStates
            }, () => {
                this.setState({ loadingBatteryData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateFrequencyData(cb) {

        try {
            let response = await getFrequencyData(this.props.site.siteId, this.state.timeframe.frame)

            let frequencyData = response
            let last = frequencyData[frequencyData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                frequencyData
            }, () => {
                this.setState({ loadingFrequencyData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateEnergyData(cb) {

        try {
            let energyData = await getEnergyData(this.props.site.siteId, this.state.timeframe.frame)

            let last = energyData[energyData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                energyData
            }, () => {
                this.setState({ loadingEnergyData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateImbalanceData(cb) {

        try {
            let imbalanceData = await getImbalance(this.props.site.siteId, this.state.timeframe.frame)

            let last = imbalanceData[imbalanceData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                imbalanceData
            }, () => {
                this.setState({ loadingImbalanceData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateImbalancePricesData(cb) {

        try {

            let imbalancePricesData = await getImbalancePrices(this.props.site.siteId, this.state.timeframe.frame)

            let last = imbalancePricesData[imbalancePricesData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                imbalancePricesData
            }, () => {
                this.setState({ loadingImbalancePricesData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateSmappeeData(cb) {

        try {
            let smappeeData = await getSmappee(this.props.site.siteId, this.state.timeframe.frame)

            let last = smappeeData[smappeeData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                smappeeData
            }, () => {
                this.setState({ loadingSmappeeData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateRevenueData(cb) {

        try {
            let revenueData = await getRevenue(this.props.site.siteId, this.state.timeframe.frame)

            let last = revenueData[revenueData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                revenueData
            }, () => {
                this.setState({ loadingRevenueData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateStatus(cb) {
        try {
            let status = await getStatus(this.props.site.siteId)

            this.setState({
                status
            }, () => {
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateFCR(cb) {
        try {
            let fcr = await getFCR(this.props.site.siteId, null /* gets today's values */)

            this.setState({
                fcr
            }, () => {
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateImbalancePriceForecastData(cb) {

        try {
            let imbalancePriceForecastData = await getDexterData(this.props.site.siteId, 'currentQuarterHour', this.state.timeframe.frame)

            if (imbalancePriceForecastData.length > 0) {
                // Adding extra element to display first bucket start
                imbalancePriceForecastData.unshift({ bucket_end: imbalancePriceForecastData[0].bucket_start })

                let highestProbBuckets = { priceUpBucket: imbalancePriceForecastData[1], priceDownBucket: imbalancePriceForecastData[1] }
                imbalancePriceForecastData.forEach(element => {

                    if (element.probs_priceup > highestProbBuckets.priceUpBucket.probs_priceup)
                        highestProbBuckets.priceUpBucket = element


                    if (element.probs_pricedown > highestProbBuckets.priceDownBucket.probs_pricedown)
                        highestProbBuckets.priceDownBucket = element
                })

                this.setState({ highestProbBuckets })
            }

            this.setState({
                imbalancePriceForecastData
            }, () => {
                this.setState({ loadingImbalancePriceForecastData: false })
                !!cb && cb()
            })

        }
        catch (err) {
            console.error(err)
        }
    }

    async updateBalancingPriceData(cb) {

        try {
            let balancingPriceData = await getDexterData(this.props.site.siteId, 'balancingPrice', this.state.timeframe.frame)

            let last = balancingPriceData[balancingPriceData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                balancingPriceData
            }, () => {
                this.setState({ loadingBalancingPriceData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    async updateImbalanceRevenueForecastData(cb) {

        try {
            let imbalanceRevenueForecastData = await getDexterData(this.props.site.siteId, 'imbalanceRevenueForecast', this.state.timeframe.frame)

            let last = imbalanceRevenueForecastData[imbalanceRevenueForecastData.length - 1]

            if (last)
                last.drawDot = true

            this.setState({
                imbalanceRevenueForecastData
            }, () => {
                this.setState({ loadingImbalanceRevenueForecastData: false })
                !!cb && cb()
            })
        }
        catch (err) {
            console.error(err)
        }
    }

    setUpdates() {

        this.unsetUpdates()

        // filter devices with defined IoT topics
        const devices = this.props.site.devices.filter(x => !!x.awsIotTopic)

        // subscribe to STOMP updtaes
        for (let i = 0; i < devices.length; i++) {

            stompSubscribe(devices[i].awsIotTopic.replace(/\//g, '.'), (data, stompSubscription) => { // convert the topic structure for the STOPM protocol (replace all slashes with dots)
                this.setState({
                    stompSubscription
                })

                if (!this.state.batteryData) return

                this.setState({
                    latestData: data,
                    batteryStates: {
                        ...this.state.batteryStates,
                        actEMSmodeExec: data.actEMSmodeExec,
                        actEMSmodeReq: data.actEMSmodeReq ? data.actEMSmodeReq : this.state.batteryStates.actEMSmodeReq,
                        SOH: data.SOH,
                        state: data.state,
                        FCRCapacityReq: data.FCRCapacityReq ? data.FCRCapacityReq : this.state.batteryStates.FCRCapacityReq,
                        FCRCapacityReqMW: data.FCRCapacityReqMW ? data.FCRCapacityReqMW : this.state.batteryStates.FCRCapacityReqMW,
                        FCRCapacityReqTime: data.time
                        // fcrStateDtlt: data.fcrStateDtlt,
                        // fcrOFF: data.fcrOFF,
                        // FCRDeliveryOK: data.FCRDeliveryOK,
                        // FCRDroopCurveOK: data.FCRDroopCurveOK,
                        // FCRCapacityOK: data.FCRCapacityOK,
                        // BaselineOK: data.BaselineOK
                    }
                })

                // update in real-time only if battery_refresh is 0
                if (this.state.timeframe.battery_refresh === 0) {

                    let batteryData = this.state.batteryData ? this.state.batteryData.map(x => {
                        delete x.drawDot
                        return x
                    }) : []

                    let frequencyData = this.state.frequencyData ? this.state.frequencyData.map(x => {
                        delete x.drawDot
                        return x
                    }) : []

                    if (data.dtlt) {

                        data.drawDot = true

                        batteryData.push(data) // add at the end the new state
                        frequencyData.push(data) // add at the end the new state

                        if (batteryData.length > 500) {
                            batteryData.splice(0, 1) // remove the first state (not relevant anymore)
                            frequencyData.splice(0, 1) // remove the first state (not relevant anymore)
                        }

                        this.setState({
                            batteryData,
                            frequencyData
                        })
                    }
                }
            })
        }

        let batteryCountdown = this.state.timeframe.battery_refresh
        let frequencyCountdown = this.state.timeframe.battery_refresh
        let energyCountdown = 900 // fixed rate of 15 minutes
        let imbalanceContdown = this.state.timeframe.imbalance_refresh
        let imbalancePricesCountdown = this.state.timeframe.imbalance_refresh
        let powerFlowsCountdown = this.state.timeframe.power_flows_refresh
        let revenueCountdown = 900 // fixed rate of 15 minutes
        let statusCountdown = 120 // fixed rate of 2 minutes
        let imbalancePriceForecastCountdown = 60 // fixed rate of 1 minute
        let balancingPriceCountdown = 60 // fixed rate of 1 minute
        let imbalanceRevenueForecastCountdown = 900 // fixed rate of 15 minutes

        // use the web worker message event to update countdown time
        this.worker.onmessage = () => {

            let hours, minutes, seconds, refresh

            // battery countdown (only if not realtime) ===============================================
            if (this.state.timeframe.battery_refresh > 0) {

                minutes = Math.floor(batteryCountdown / 60)
                seconds = batteryCountdown % 60

                if (minutes < 10) minutes = '0' + minutes
                if (seconds < 10) seconds = '0' + seconds

                refresh = minutes + ':' + seconds

                document.querySelectorAll('.chart-battery tspan').forEach(el => {
                    el.innerHTML = batteryCountdown > 0 ? refresh : 'updating'
                })

                if (batteryCountdown === 0) {

                    this.updateBatteryData(() => {
                        batteryCountdown = this.state.timeframe.battery_refresh
                    })
                }

                batteryCountdown--
            }
            // ========================================================================================

            // frequency countdown (only if not realtime) ===============================================
            if (this.state.timeframe.battery_refresh > 0) {

                minutes = Math.floor(frequencyCountdown / 60)
                seconds = frequencyCountdown % 60

                if (minutes < 10) minutes = '0' + minutes
                if (seconds < 10) seconds = '0' + seconds

                refresh = minutes + ':' + seconds

                document.querySelectorAll('.chart-frequency tspan').forEach(el => {
                    el.innerHTML = frequencyCountdown > 0 ? refresh : 'updating'
                })

                if (frequencyCountdown === 0) {

                    this.updateFrequencyData(() => {
                        frequencyCountdown = this.state.timeframe.battery_refresh
                    })
                }

                frequencyCountdown--
            }
            // ========================================================================================

            // energy coutdown ========================================================================
            minutes = Math.floor(energyCountdown / 60)
            seconds = energyCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-energy tspan').forEach(el => {
                el.innerHTML = energyCountdown > 0 ? refresh : 'updating'
            })

            if (energyCountdown === 0) {

                this.updateEnergyData(() => {
                    energyCountdown = 900
                })
            }

            energyCountdown--
            // ========================================================================================


            // imbalance countdown ====================================================================
            minutes = Math.floor(imbalanceContdown / 60)
            seconds = imbalanceContdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-imbalance tspan').forEach(el => {
                el.innerHTML = imbalanceContdown > 0 ? refresh : 'updating'
            })

            if (imbalanceContdown === 0) {

                this.updateImbalanceData(() => {
                    imbalanceContdown = this.state.timeframe.imbalance_refresh
                })
            }

            imbalanceContdown--
            // ========================================================================================


            // imbalance prices countdown =============================================================
            minutes = Math.floor(imbalancePricesCountdown / 60)
            seconds = imbalancePricesCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-imbalance-prices tspan').forEach(el => {
                el.innerHTML = imbalancePricesCountdown > 0 ? refresh : 'updating'
            })

            if (imbalancePricesCountdown === 0) {

                this.updateImbalancePricesData(() => {
                    imbalancePricesCountdown = this.state.timeframe.imbalance_refresh
                })
            }

            imbalancePricesCountdown--
            // ========================================================================================


            // power flows countdown ==================================================================
            hours = Math.floor(powerFlowsCountdown / 3600)
            minutes = Math.floor((powerFlowsCountdown - (hours * 3600)) / 60)
            seconds = powerFlowsCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = (hours > 0 ? hours + ':' : '') + minutes + ':' + seconds

            document.querySelectorAll('.chart-power-flows tspan').forEach(el => {
                el.innerHTML = powerFlowsCountdown > 0 ? refresh : 'updating'
            })

            if (powerFlowsCountdown === 0) {

                this.updateSmappeeData(() => {
                    powerFlowsCountdown = this.state.timeframe.power_flows_refresh
                })
            }

            powerFlowsCountdown--
            // ========================================================================================


            // revenues countdown =====================================================================
            minutes = Math.floor(revenueCountdown / 60)
            seconds = revenueCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-revenue tspan').forEach(el => {
                el.innerHTML = revenueCountdown > 0 ? refresh : 'updating'
            })

            if (revenueCountdown === 0) {

                this.updateRevenueData(() => {
                    revenueCountdown = 900
                })
            }

            revenueCountdown--
            // ========================================================================================


            // status countdown =======================================================================
            minutes = Math.floor(statusCountdown / 60)
            seconds = statusCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-gateway-status').forEach(el => {
                el.innerHTML = statusCountdown > 0 ? refresh : 'updating'
            })

            if (statusCountdown === 0) {

                this.updateStatus(() => {
                    statusCountdown = 120
                })
            }

            statusCountdown--
            // ========================================================================================


            // imbalance price forecast countdown =======================================================================
            minutes = Math.floor(imbalancePriceForecastCountdown / 60)
            seconds = imbalancePriceForecastCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-imbalancePriceForecast').forEach(el => {
                el.innerHTML = imbalancePriceForecastCountdown > 0 ? refresh : 'updating'
            })

            if (imbalancePriceForecastCountdown === 0) {

                this.updateImbalancePriceForecastData(() => {
                    imbalancePriceForecastCountdown = 60
                })
            }

            imbalancePriceForecastCountdown--
            // // ========================================================================================


            // balancing price countdown =======================================================================
            minutes = Math.floor(balancingPriceCountdown / 60)
            seconds = balancingPriceCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-balancingPrice').forEach(el => {
                el.innerHTML = balancingPriceCountdown > 0 ? refresh : 'updating'
            })

            if (balancingPriceCountdown === 0) {

                this.updateBalancingPriceData(() => {
                    balancingPriceCountdown = 60
                })
            }

            balancingPriceCountdown--
            // ========================================================================================


            // imbalance revenue forecast countdown =======================================================================
            minutes = Math.floor(imbalanceRevenueForecastCountdown / 60)
            seconds = imbalanceRevenueForecastCountdown % 60

            if (minutes < 10) minutes = '0' + minutes
            if (seconds < 10) seconds = '0' + seconds

            refresh = minutes + ':' + seconds

            document.querySelectorAll('.chart-imbalanceRevenueForecast').forEach(el => {
                el.innerHTML = imbalanceRevenueForecastCountdown > 0 ? refresh : 'updating'
            })

            if (imbalanceRevenueForecastCountdown === 0) {

                this.updateImbalanceRevenueForecastData(() => {
                    imbalanceRevenueForecastCountdown = 900
                })
            }

            imbalanceRevenueForecastCountdown--
            // ========================================================================================
        }
    }

    unsetUpdates() {

        stompUnsubscribe(this.state.stompSubscription)
        this.setState({
            stompSubscription: undefined
        })
    }

    tickFormatterX(tick, format) {
        const time = moment(tick, 'YYYY-MM-DD HH:mm:ss')

        if (this.state.timeframe.frame === '1d')
            return time.format('Y-MM-DD H:mm')

        if (!this.state.timeframe.seconds)
            return time.format('H:mm')

        return time.format(format || 'H:mm:ss')
    }

    tickFormatterY(tick) {
        return parseFloat(tick).toFixed()
    }

    onTimeframeChange(e) {

        this.unsetUpdates()

        this.setState({
            timeframe: timeframes.find(x => x.frame === e.target.value),
            loadingBatteryData: true,
            loadingEnergyData: true,
            loadingImbalanceData: true,
            loadingImbalancePricesData: true,
            loadingSmappeeData: true,
            loadingRevenueData: true,
            loadingImbalancePriceForecastData: true,
            loadingBalancingPriceData: true,
            loadingImbalanceRevenueForecastData: true
        }, () => {
            this.updateBatteryData()
            this.updateFrequencyData()
            this.updateEnergyData()
            this.updateImbalanceData()
            this.updateImbalancePricesData()
            this.updateSmappeeData()
            this.updateRevenueData()
            this.updateImbalancePriceForecastData()
            this.updateBalancingPriceData()
            this.updateImbalanceRevenueForecastData()
            this.setUpdates()
            window.localStorage.timeframe = JSON.stringify(this.state.timeframe)
        })
    }

    toggleChart(e, i, el) {
        el.target.classList.toggle('text-muted')

        let unit = e.payload.unit

        if (unit && unit.endsWith('h'))
            unit = unit.substring(0, unit.length - 1)

        let key = e.dataKey.trim()
        let index = key.lastIndexOf(unit)

        if (index > 0)
            key = key.substring(0, index)

        this.setState({
            hidden: { ...this.state.hidden, [key]: !this.state.hidden[key] }
        })
    }

    toggleDynamicYAxis(e, key) {
        this.setState({
            dynamicYAxis: { ...this.state.dynamicYAxis, [key]: e.target.checked }
        })
    }

    getLastUpdate() {

        let status = 'Status: Waiting...', updateOK = false

        if (this.state.latestData) {

            const date = moment(this.state.latestData.dtlt, 'YYYY-MM-DD HH:mm:ss')

            updateOK = moment(this.state.latestData.dtlt, 'YYYY-MM-DD HH:mm:ss').toDate() > moment().subtract(5, 'second').toDate()

            if (updateOK)
                status = 'Status: All good'
            else
                status = `Status: Not updated ${date.toNow()}`
        }

        return <div className={`alert ${updateOK ? 'alert-success' : 'alert-warning'}`}>{status}</div>
    }

    setDot(dot, key, unit, decimals, min, max, className, data) {

        let boxWidth = 110
        // if unit is kW we need to display wider box
        if (unit.indexOf('kW') > -1)
            boxWidth = 130

        if (className === 'chart-balancingPrice')
            boxWidth = 130

        // if price, incease the width additionally
        if (unit.indexOf('€') > -1)
            boxWidth += 10

        if (dot.payload) {

            if (dot.payload.drawDot) {

                let refresh = 'updating'
                if (this.state.timeframe.battery_refresh === 0 && !dot.payload.imbalance)
                    refresh = 'realtime'

                let offsetVR = -60,
                    offsetVT = -37.5,
                    offsetCR = -23.5,
                    offsetCT = -10

                if (data) {
                    const dataMin = data.reduce((min, p) => p[key] < min ? p[key] : min, min)
                    if (dataMin < min)
                        min = dataMin

                    const dataMax = data.reduce((max, p) => p[key] > max ? p[key] : max, max)
                    if (dataMax > max)
                        max = dataMax
                }

                const range = parseFloat(Math.abs(max - min).toFixed(2))

                let value = dot.payload[key]

                value = value || 0
                value = parseFloat(value)

                // fix position of the value and counter boxes if out of bounds
                if (value > max - (range / 4)) {
                    offsetVR = 4
                    offsetVT = 27.5
                    offsetCR = 40.5
                    offsetCT = 55
                }

                return (
                    // group the SVG elements
                    <g key={dot.key}>
                        <Rectangle x={dot.cx - boxWidth} y={dot.cy + offsetVR} width={boxWidth} height={35} fill="#008cba" />
                        <Text x={dot.cx - 12.5} y={dot.cy + offsetVT} style={{ fontSize: '1rem' }} fill="#fff" textAnchor="end">{value.toFixed(decimals) + ' ' + unit}</Text>

                        <Rectangle x={dot.cx - 75} y={dot.cy + offsetCR} width={75} height={20} fill="#008cba" />
                        <Text x={dot.cx - 12.5} y={dot.cy + offsetCT} style={{ fontSize: '0.8rem' }} fill="#fff" textAnchor="end" className={className}>{refresh}</Text>

                        <Dot r={5} stroke="#008cba" strokeWidth={3} fill="#008cba" fillOpacity={0.1} cx={dot.cx} cy={dot.cy} />
                    </g>
                )
            }
        }
        else {

            if (data.length - 1 === dot.index) {

                let refresh = 'updating'

                let offsetVR = -60,
                    offsetVT = -37.5,
                    offsetCR = -23.5,
                    offsetCT = -10

                const range = parseFloat(Math.abs(max - min).toFixed(2))

                let value = dot.value

                value = value || 0
                value = parseFloat(value)

                // fix position of the value and counter boxes if out of bounds
                if (value > max - (range / 4)) {
                    offsetVR = 4
                    offsetVT = 27.5
                    offsetCR = 40.5
                    offsetCT = 55
                }

                return (
                    <g>
                        <Rectangle x={dot.x + (dot.width / 2) - boxWidth} y={dot.y + offsetVR} width={boxWidth} height={35} fill="#008cba" />
                        <Text x={dot.x + (dot.width / 2) - 12.5} y={dot.y + offsetVT} style={{ fontSize: '1rem' }} fill="#fff" textAnchor="end">{value.toFixed(decimals) + ' ' + unit}</Text>

                        <Rectangle x={dot.x + (dot.width / 2) - 75} y={dot.y + offsetCR} width={75} height={20} fill="#008cba" />
                        <Text x={dot.x + (dot.width / 2) - 12.5} y={dot.y + offsetCT} style={{ fontSize: '0.8rem' }} fill="#fff" textAnchor="end" className={className}>{refresh}</Text>

                        <Dot r={5} stroke="#008cba" strokeWidth={3} fill="#008cba" fillOpacity={0.1} cx={dot.x + (dot.width / 2)} cy={dot.y} />
                    </g>
                )
            }
        }
    }

    printValue(allocation, type) {

        let data

        switch (type) {

            case 'volume':
                data = allocation[`allocated_capacity${this.props.unit === 'kW' ? '' : 'MW'}`]
                break

            case 'price':
                data = allocation.cleared_price_wavg
                break

            default:
                data = '/'
                break
        }

        switch (allocation.generated_by) {

            case 'AES':
                // AES data say as it is received
                break

            case 'Lambda':
                // Lambda generated data
                data = data || '0'
                break

            case 'Flex':
                data = '/' + (type === 'volume' ? ' (0)' : '')
                break

            default:
                // Data entered manually stays as it is
                break
        }

        return data || ''
    }

    getTooltip(allocation, type) {

        let tooltip

        switch (allocation.generated_by) {

            case 'AES':
                tooltip = 'Auction data received by AES'
                break

            case 'Lambda':
                tooltip = 'Auction data auto-generated with Lambda'
                break

            case 'Flex':
                tooltip = 'Auction data missing for this block' + (type === 'volume' ? ' (nominated as 0)' : '')
                break

            default:
                tooltip = 'Auction data maually generated by ' + allocation.generated_by
                break
        }

        return tooltip
    }

    render() {

        if (!this.props.site)
            return <Loader className="container align-items-center" noText={true} />

        if (this.props.site.battery === null)
            return <div className="row d-flex justify-content-center">
                <div className="col-sm-6 text-center py-5">
                    <span className="display-1">
                        <i className="fas fa-exclamation-triangle text-danger"></i>
                    </span>
                    <h1>You have not yet connected battery to {this.props.site.siteName}</h1>
                    <p className="h5 pb-5 mb-5">
                        A battery system, linked to local production, can avoid distribution and transport costs of your energy. Yuso helps you with dimensioning, financing and market-driven control of your battery system. Interested? Contact us at <a href="mailto:info@yuso.be">info@yuso.be</a>.
                    </p>
                </div>
            </div>

        const charts = this.props.site.battery.visualisationConfig.monitoring || {}

        return <div className="row">
            <div className="col-sm">
                <h1 className="text-center">
                    {this.props.site.siteName} Monitoring Dashboard
                </h1>
                <h4 className="text-center text-muted">
                    Yuso Inside BESS Application
                </h4>

                {/* TIMEFRAMES SELECTORS */}
                <div className="row mb-3">
                    <div className="col-sm d-flex justify-content-center">
                        <div className="btn-group btn-group-toggle d-flex flex-wrap">
                            {
                                timeframes.map(t => {
                                    return <label className={`btn btn-outline-primary mr-2 mb-2 btn-timeframe cursor pointer ${t.frame === this.state.timeframe.frame ? 'active' : ''}`} key={t.frame}>
                                        <input type="radio" name="timeframe" value={t.frame} checked={t.frame === this.state.timeframe.frame} onChange={this.onTimeframeChange} autoComplete="off" /> {t.frame}
                                    </label>
                                })
                            }
                        </div>
                    </div>
                </div>

                {
                    !charts.includes("aesInfoBoxes") && !charts.includes("battStateInfoBoxes") ? null :
                        <div className='group-card pt-3 px-3 mb-4'>
                            <div className="row">
                                {
                                    !charts.includes("aesInfoBoxes") ? null :
                                        [
                                            <div className="col-sm col-md-6" key={0}>
                                                <div className="card border-0 mb-4">
                                                    <div className="card-body">
                                                        <h5 className="card-title text-center">FCR Allocated Capacity ({this.props.unit})</h5>
                                                        <div className="card-text">
                                                            {
                                                                !this.state.fcr ?
                                                                    <Loader>Loading...</Loader>
                                                                    :
                                                                    !this.state.fcr.length ?
                                                                        <span>No FCR prices received for today</span>
                                                                        :
                                                                        <table className="table table-sm table-bordered">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Block</th>
                                                                                    {
                                                                                        this.state.fcr.map(x => <th className="text-center" key={x.start_time}>{x.start_time} - {x.end_time}</th>)
                                                                                    }
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                <tr>
                                                                                    <td>Volume</td>
                                                                                    {
                                                                                        this.state.fcr.map(x => <td className="text-center" title={this.getTooltip(x, 'volume')} key={x.start_time}>{this.printValue(x, 'volume')}</td>)
                                                                                    }
                                                                                </tr>
                                                                            </tbody>
                                                                        </table>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>,
                                            <div className="col-sm col-md-6" key={1}>
                                                <div className="card border-0 mb-4">
                                                    <div className="card-body">
                                                        <h5 className="card-title text-center">FCR Auction Price (€/MWh)</h5>
                                                        <div className="card-text">
                                                            {
                                                                !this.state.fcr ?
                                                                    <Loader>Loading...</Loader>
                                                                    :
                                                                    !this.state.fcr.length ?
                                                                        <span>No FCR prices received for today</span>
                                                                        :
                                                                        <table className="table table-sm table-bordered">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Block</th>
                                                                                    {
                                                                                        this.state.fcr.map(x => <th className="text-center" key={x.start_time}>{x.start_time} - {x.end_time}</th>)
                                                                                    }
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                <tr>
                                                                                    <td>Volume</td>
                                                                                    {
                                                                                        this.state.fcr.map(x => <td className="text-center" title={this.getTooltip(x, 'price')} key={x.start_time}>{this.printValue(x, 'price')}</td>)
                                                                                    }
                                                                                </tr>
                                                                            </tbody>
                                                                        </table>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ]
                                }
                                {
                                    !charts.includes("battStateInfoBoxes") ? null :
                                        [
                                            <div className="col-sm col-md-4" key={0}>
                                                <div className="card border-0 mb-4">
                                                    <div className="card-body">
                                                        <h5 className="card-title text-center">ActiveEMSmode</h5>
                                                        <div className="card-text">
                                                            {
                                                                !this.state.batteryStates ?
                                                                    <Loader>Loading...</Loader>
                                                                    :
                                                                    <h4 className="text-center font-weight-bold">{this.state.batteryStates.actEMSmodeExec || (0, '0', 'standby').includes(this.state.batteryStates.actEMSmodeExec) ? this.state.batteryStates.actEMSmodeExec : 'N/A'}</h4>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>,
                                            <div className="col-sm col-md-4" key={1}>
                                                <div className="card border-0 mb-4">
                                                    <div className="card-body">
                                                        <h5 className="card-title text-center">Active EMS Mode Req</h5>
                                                        <div className="card-text">
                                                            {
                                                                !this.state.batteryStates ?
                                                                    <Loader>Loading...</Loader>
                                                                    :
                                                                    <h4 className="text-center font-weight-bold">{this.state.batteryStates.actEMSmodeReq || (0, '0', 'standby').includes(this.state.batteryStates.actEMSmodeExec) ? this.state.batteryStates.actEMSmodeReq : 'N/A'}</h4>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>,
                                            <div className="col-sm col-md-4" key={2}>
                                                <div className="card border-0 mb-4">
                                                    <div className="card-body">
                                                        <h5 className="card-title text-center">Battery State</h5>
                                                        <div className="card-text">
                                                            {
                                                                !this.state.batteryStates ?
                                                                    <Loader>Loading...</Loader>
                                                                    :
                                                                    <h4 className="text-center font-weight-bold">{this.state.batteryStates.state ? this.state.batteryStates.state : 'N/A'}</h4>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ]
                                }
                            </div>
                        </div>
                }

                {
                    this.props.site.siteId === 'aes' ? null :
                        <div className='group-card pt-3 px-3 mb-4'>
                            <h2 className="text-center">General overview</h2><hr />
                            < div className="row" >
                                <div className="col-sm col-lg-12 col-xl-6">
                                    <ActivePower
                                        loadingBatteryData={this.state.loadingBatteryData}
                                        assetSpecs={this.props.site.battery.assetSpecs}
                                        unit={this.props.unit}
                                        batteryData={this.state.batteryData}
                                        hidden={this.state.hidden}
                                        dynamicYAxis={this.state.dynamicYAxis}
                                        toggleDynamicYAxis={this.toggleDynamicYAxis}
                                        tickFormatterX={this.tickFormatterX}
                                        tickFormatterY={this.tickFormatterY}
                                        toggleChart={this.toggleChart}
                                        setDot={this.setDot}
                                    />
                                </div>
                                <div className="col-sm col-lg-12 col-xl-6">
                                    <AvailableDischargeEnergy
                                        loadingBatteryData={this.state.loadingBatteryData}
                                        assetSpecs={this.props.site.battery.assetSpecs}
                                        unit={this.props.unit}
                                        batteryData={this.state.batteryData}
                                        hidden={this.state.hidden}
                                        dynamicYAxis={this.state.dynamicYAxis}
                                        toggleDynamicYAxis={this.toggleDynamicYAxis}
                                        tickFormatterX={this.tickFormatterX}
                                        tickFormatterY={this.tickFormatterY}
                                        toggleChart={this.toggleChart}
                                        setDot={this.setDot}
                                    />
                                </div>
                            </div>
                        </div>
                }
                {
                    !charts.includes("FCR") ? null :
                        <div className='group-card pt-3 px-3 mb-4'>
                            <h2 className="text-center">Frequency Containment Service</h2><hr />
                            < div className="row justify-content-center" >
                                <div className="col-sm col-lg-6">
                                    <div className="card border-0 mb-4">
                                        <div className="card-body">
                                            <h5 className="card-title text-center">FCR Capacity Allocated</h5>
                                            <div className="card-text">
                                                {
                                                    !this.state.batteryStates ?
                                                        <Loader>Loading...</Loader>
                                                        :
                                                        <h4 className={`text-center font-weight-bold`}>{(this.props.unit === 'kW' ? this.state.batteryStates.FCRCapacityReq : this.state.batteryStates.FCRCapacityReqMW) || 'N/A'}
                                                            <span><i className="fa fa-info-circle ml-1 text-info" data-toggle="tooltip" data-boundary="viewport" data-html="true" title={`${this.state.batteryStates.FCRCapacityReqTime}`}></i></span>
                                                        </h4>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm col-lg-6">
                                    <div className="card border-0 mb-4">
                                        <div className="card-body">
                                            <h5 className="card-title text-center">FCR Delivery State</h5>
                                            <div className="card-text">
                                                {
                                                    !this.state.batteryStates ?
                                                        <Loader>Loading...</Loader>
                                                        :
                                                        this.state.batteryStates.fcrOFF
                                                            ? <h4 className={`text-center font-weight-bold`}>FCR delivery not required
                                                                <span><i className="fa fa-info-circle ml-1 text-info" data-toggle="tooltip" data-boundary="viewport" data-html="true" title={`${this.state.batteryStates.fcrStateDtlt ? this.state.batteryStates.fcrStateDtlt : "Time N/A"}`}></i></span>
                                                            </h4>
                                                            : <h4 className={`text-center font-weight-bold text-${this.state.batteryStates.FCRDeliveryOK ? "success" : "danger"}`}>{this.state.batteryStates.FCRDeliveryOK ? "FCR Delivery OK" : "FCR Delivery NOK"}
                                                                <span><i className="fa fa-info-circle ml-1 text-info" data-toggle="tooltip" data-boundary="viewport" data-html="true" title={`${this.state.batteryStates.fcrStateDtlt ? this.state.batteryStates.fcrStateDtlt : "Time N/A"}
${this.state.batteryStates.FCRDroopCurveOK ? "FCR Droop Curve OK" : "FCR Droop Curve NOK"}
${this.state.batteryStates.FCRCapacityOK ? "FCR Capacity OK" : "FCR Capacity NOK"}
${this.state.batteryStates.BaselineOK ? "Baseline OK" : "Baseline NOK"}
                                                                `}></i></span>
                                                            </h4>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm col-lg-12 col-xl-6">
                                    <FrequencyResponse
                                        site={this.props.site}
                                        loadingFrequencyData={this.state.loadingFrequencyData}
                                        unit={this.props.unit}
                                        frequencyData={this.state.frequencyData}
                                        hidden={this.state.hidden}
                                        dynamicYAxis={this.state.dynamicYAxis}
                                        toggleDynamicYAxis={this.toggleDynamicYAxis}
                                        tickFormatterX={this.tickFormatterX}
                                        tickFormatterY={this.tickFormatterY}
                                        toggleChart={this.toggleChart}
                                        setDot={this.setDot}
                                    />
                                </div>

                                <div className="col-sm col-lg-12 col-xl-6">
                                    <GridFrequency
                                        loadingFrequencyData={this.state.loadingFrequencyData}
                                        unit={this.props.unit}
                                        frequencyData={this.state.frequencyData}
                                        hidden={this.state.hidden}
                                        dynamicYAxis={this.state.dynamicYAxis}
                                        toggleDynamicYAxis={this.toggleDynamicYAxis}
                                        tickFormatterX={this.tickFormatterX}
                                        tickFormatterY={this.tickFormatterY}
                                        toggleChart={this.toggleChart}
                                        setDot={this.setDot}
                                    />
                                </div>

                                <div className="col-sm col-lg-12 col-xl-6">
                                    <BaselinePowerSOC
                                        site={this.props.site}
                                        loadingFrequencyData={this.state.loadingFrequencyData}
                                        unit={this.props.unit}
                                        frequencyData={this.state.frequencyData}
                                        hidden={this.state.hidden}
                                        dynamicYAxis={this.state.dynamicYAxis}
                                        toggleDynamicYAxis={this.toggleDynamicYAxis}
                                        tickFormatterX={this.tickFormatterX}
                                        tickFormatterY={this.tickFormatterY}
                                        toggleChart={this.toggleChart}
                                        setDot={this.setDot}
                                    />
                                </div>

                            </div>
                        </div>
                }
                {
                    !charts.includes("BatteryPower") && !charts.includes("CurrentquarterhourImbalancePriceForecast") && !charts.includes("StateOfCharge") && !charts.includes("GridPower") && !charts.includes("Imbalanceforecastaccuracy") && !charts.includes("Frequency") && !charts.includes("SystemImbalanceandRegulatingPower") && !charts.includes("Energy") && !charts.includes("ImbalanceRevenueForecast") && !charts.includes("EnergyPrices") && !charts.includes("NetBatteryPnL") && !charts.includes("BatteryPnLonEliaImbalanceMechanism") && !charts.includes("Status") ? null :
                        <div className='group-card pt-3 px-3 mb-4'>
                            {/* <h2 className="text-center"></h2><hr /> */}
                            < div className="row justify-content-center">
                                {
                                    !charts.includes("BatteryPower") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            {/* TBD (separation of types)*/}
                                            <Power
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingBatteryData={this.state.loadingBatteryData}
                                                batteryData={this.state.batteryData}
                                                type="power"
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("CurrentquarterhourImbalancePriceForecast") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <ImbalancePriceForecast
                                                site={this.props.site}
                                                loadingImbalancePriceForecastData={this.state.loadingImbalancePriceForecastData}
                                                imbalancePriceForecastData={this.state.imbalancePriceForecastData}
                                                highestProbBuckets={this.state.highestProbBuckets}
                                                hidden={this.state.hidden}
                                                toggleChart={this.toggleChart}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("StateOfCharge") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <StateOfCharge
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingBatteryData={this.state.loadingBatteryData}
                                                batteryData={this.state.batteryData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("GridPower") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <Power
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingBatteryData={this.state.loadingBatteryData}
                                                batteryData={this.state.batteryData}
                                                type="gridActPwr"
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("Imbalanceforecastaccuracy") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <BalancingPrice
                                                site={this.props.site}
                                                loadingBalancingPriceData={this.state.loadingBalancingPriceData}
                                                balancingPriceData={this.state.balancingPriceData}
                                                hidden={this.state.hidden}
                                                tickFormatterX={this.tickFormatterX}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("Frequency") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <Frequency
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingBatteryData={this.state.loadingBatteryData}
                                                batteryData={this.state.batteryData}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                hidden={this.state.hidden}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("SystemImbalanceandRegulatingPower") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <Imbalance
                                                site={this.props.site}
                                                unit="MW" //unit is always MW
                                                loadingImbalanceData={this.state.loadingImbalanceData}
                                                imbalanceData={this.state.imbalanceData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("Energy") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <Energy
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingEnergyData={this.state.loadingEnergyData}
                                                energyData={this.state.energyData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("ImbalanceRevenueForecast") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <ImbalanceRevenueForecast
                                                site={this.props.site}
                                                loadingImbalanceRevenueForecastData={this.state.loadingImbalanceRevenueForecastData}
                                                imbalanceRevenueForecastData={this.state.imbalanceRevenueForecastData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {/* {
                        // TBD (siteConfig and add to templates)
                        this.props.site.siteConfig.grid &&
                            this.props.site.siteConfig.grid.consumption &&
                            this.props.site.siteConfig.grid.consumption.metering &&
                            this.props.site.siteConfig.grid.consumption.metering.find(x => x.type === 'Smappee').id  // Devices with Smappee meter 
                             ?
                            <PowerFlows
                                site={this.props.site}
                                unit={this.props.unit}
                                smappeeData={this.state.smappeeData}
                                loadingSmappeeData={this.state.loadingSmappeeData}
                                hidden={this.state.hidden}
                                dynamicYAxis={this.state.dynamicYAxis}
                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                tickFormatterX={this.tickFormatterX}
                                tickFormatterY={this.tickFormatterY}
                                toggleChart={this.toggleChart}
                                setDot={this.setDot}
                            />
                            : null
                    } */}
                                {
                                    !charts.includes("EnergyPrices") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <ImbalancePrices
                                                site={this.props.site}
                                                timeframe={this.state.timeframe}
                                                unit={this.props.unit}
                                                loadingImbalancePricesData={this.state.loadingImbalancePricesData}
                                                imbalancePricesData={this.state.imbalancePricesData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("NetBatteryPnL") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <Revenue
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingRevenueData={this.state.loadingRevenueData}
                                                revenueData={this.state.revenueData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                                {
                                    !charts.includes("BatteryPnLonEliaImbalanceMechanism") ? null :
                                        <div className="col-sm col-lg-12 col-xl-6">
                                            <ImbalanceRevenue
                                                site={this.props.site}
                                                unit={this.props.unit}
                                                loadingRevenueData={this.state.loadingRevenueData}
                                                revenueData={this.state.revenueData}
                                                hidden={this.state.hidden}
                                                dynamicYAxis={this.state.dynamicYAxis}
                                                toggleDynamicYAxis={this.toggleDynamicYAxis}
                                                tickFormatterX={this.tickFormatterX}
                                                tickFormatterY={this.tickFormatterY}
                                                toggleChart={this.toggleChart}
                                                setDot={this.setDot}
                                            />
                                        </div>
                                }
                            </div>
                            <div className='row mb-3'>
                                {
                                    !charts.includes("Status") ? null :
                                        <Status
                                            site={this.props.site}
                                            latestData={this.state.latestData}
                                            status={this.state.status}
                                            getLastUpdate={this.getLastUpdate}
                                            tickFormatterX={this.tickFormatterX}
                                            tickFormatterY={this.tickFormatterY}
                                            toggleChart={this.toggleChart}
                                        />
                                }
                            </div >
                        </div>
                }
            </div>
        </div >
    }
}

export default Monitoring
